import { useEffect, useState, useCallback, useRef } from 'react';
import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { apiDownloadUrl, apiUpload } from '../../lib/api';
import { ImageCropModal } from './ImageCropModal';
import { cropImageToFile, type PixelCrop } from './cropImage';

/** Sources we must authed-fetch (server-served, auth-required). */
function needsAuthedLoad(src: string): boolean {
  return src.startsWith('/api/images/');
}

export function ImageNodeView(props: NodeViewProps) {
  const { node, updateAttributes, selected, editor } = props;
  const { src, alt, width, caption } = node.attrs as {
    src: string; alt: string | null; width: number | null; caption: string | null;
  };

  const [displayUrl, setDisplayUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [panel, setPanel] = useState<'none' | 'alt' | 'caption'>('none');
  const [cropOpen, setCropOpen] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const showControls = selected && editor.isEditable;

  const load = useCallback(() => {
    let revoked: string | null = null;
    let cancelled = false;
    setStatus('loading');
    if (!src) { setStatus('error'); return () => {}; }
    if (!needsAuthedLoad(src)) {
      setDisplayUrl(src);
      setStatus('ready');
      return () => {};
    }
    apiDownloadUrl(src.replace(/^\/api/, ''))
      .then((url) => {
        if (cancelled) { URL.revokeObjectURL(url); return; }
        revoked = url;
        setDisplayUrl(url);
        setStatus('ready');
      })
      .catch(() => { if (!cancelled) setStatus('error'); });
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [src]);

  useEffect(() => load(), [load]);

  const onResizePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = imgRef.current?.getBoundingClientRect().width ?? width ?? 200;
    const containerWidth = imgRef.current?.parentElement?.getBoundingClientRect().width ?? Infinity;
    const onMove = (ev: PointerEvent) => {
      const next = Math.round(startWidth + (ev.clientX - startX));
      const clamped = Math.max(80, Math.min(next, containerWidth || next));
      updateAttributes({ width: clamped });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [updateAttributes, width]);

  const onConfirmCrop = useCallback(async (cropPixels: PixelCrop) => {
    setCropOpen(false);
    if (!displayUrl) return;
    try {
      const file = await cropImageToFile(displayUrl, cropPixels, 'cropped.png', 'image/png');
      const form = new FormData();
      form.append('file', file);
      const { url } = await apiUpload<{ url: string }>('/images', form);
      updateAttributes({ src: url });
    } catch (err) {
      console.error('Image crop failed:', err);
      props.extension.options.onError?.('Failed to crop image');
    }
  }, [displayUrl, updateAttributes, props.extension.options]);

  return (
    <NodeViewWrapper as="figure" className="image-nodeview" data-status={status}>
      {showControls && (
        <div className="image-nodeview-toolbar" contentEditable={false}>
          <button type="button" aria-label="Alt text" onClick={() => setPanel(panel === 'alt' ? 'none' : 'alt')}>Alt</button>
          <button type="button" aria-label="Caption" onClick={() => setPanel(panel === 'caption' ? 'none' : 'caption')}>Caption</button>
          <button type="button" aria-label="Crop image" onClick={() => setCropOpen(true)}>Crop</button>
          <button type="button" aria-label="Delete image" onClick={() => props.deleteNode()}>Delete</button>
        </div>
      )}
      {showControls && panel === 'alt' && (
        <input
          className="image-nodeview-field"
          placeholder="Describe the image (alt text)"
          defaultValue={alt ?? ''}
          contentEditable={false}
          onChange={(e) => updateAttributes({ alt: e.target.value })}
        />
      )}
      {showControls && panel === 'caption' && (
        <input
          className="image-nodeview-field"
          placeholder="Add a caption"
          defaultValue={caption ?? ''}
          contentEditable={false}
          onChange={(e) => updateAttributes({ caption: e.target.value === '' ? null : e.target.value })}
        />
      )}
      {status === 'loading' && <div className="image-nodeview-skeleton" aria-label="Loading image" />}
      {status === 'error' && (
        <div className="image-nodeview-error">
          <span>Image failed to load.</span>
          <button type="button" onClick={() => load()}>Retry</button>
        </div>
      )}
      {status === 'ready' && displayUrl && (
        <span className="image-nodeview-imgwrap">
          <img
            ref={imgRef}
            src={displayUrl}
            alt={alt ?? ''}
            role="img"
            draggable={false}
            style={{ width: width ? `${width}px` : undefined, maxWidth: '100%' }}
          />
          {showControls && (
            <span
              data-testid="resize-handle-se"
              className="image-nodeview-handle image-nodeview-handle-se"
              onPointerDown={onResizePointerDown}
              contentEditable={false}
            />
          )}
        </span>
      )}
      {caption != null && caption !== '' && (
        <figcaption className="image-nodeview-caption">{caption}</figcaption>
      )}
      {cropOpen && displayUrl && (
        <ImageCropModal imageUrl={displayUrl} onCancel={() => setCropOpen(false)} onConfirm={onConfirmCrop} />
      )}
    </NodeViewWrapper>
  );
}
